import React, { useState, useMemo, CSSProperties } from 'react';
import { QuestionFolder } from '@/types';

interface MoveToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (targetFolderId: string | null) => Promise<void>;
  folders: QuestionFolder[];
  currentFolderId?: string | null;
  disabledFolderIds?: string[]; // Folders that cannot be selected (e.g., the item being moved and its descendants)
  itemName: string; // Name of the item being moved
  itemType: 'question set' | 'folder';
}

interface FolderTreeNode {
  folder: QuestionFolder | null; // null for root
  children: FolderTreeNode[];
}

export const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  isOpen,
  onClose,
  onMove,
  folders,
  currentFolderId,
  disabledFolderIds = [],
  itemName,
  itemType
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(currentFolderId || null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Build folder tree structure
  const folderTree = useMemo(() => {
    const buildTree = (parentId: string | null): FolderTreeNode[] => {
      return folders
        .filter((f) => (f.parentFolderId || null) === parentId)
        .map((folder) => ({
          folder,
          children: buildTree(folder.id)
        }));
    };

    return buildTree(null);
  }, [folders]);

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onMove(selectedFolderId);
      onClose();
    } catch (err: unknown) {
      console.error('Failed to move item:', err instanceof Error ? err.message : String(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFolderTree = (nodes: FolderTreeNode[], depth: number = 0) => {
    return nodes.map(({ folder, children }) => {
      if (!folder) return null;

      const isDisabled = disabledFolderIds.includes(folder.id);
      const isExpanded = expandedFolders.has(folder.id);
      const isSelected = selectedFolderId === folder.id;
      const hasChildren = children.length > 0;

      const folderItemStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        borderRadius: '12px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        marginLeft: `${depth * 20}px`,
        opacity: isDisabled ? 0.4 : 1,
        background: isSelected ? 'rgba(102, 126, 234, 0.15)' : 'transparent',
        border: isSelected ? '2px solid #667eea' : '2px solid transparent',
        userSelect: 'none'
      };

      const expandButtonStyle: CSSProperties = {
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.75rem',
        padding: 0,
        transition: 'color 0.2s ease'
      };

      return (
        <div key={folder.id}>
          <div
            style={folderItemStyle}
            onClick={() => !isDisabled && setSelectedFolderId(folder.id)}
            onMouseEnter={(e) => {
              if (!isDisabled && !isSelected) {
                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDisabled && !isSelected) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            {/* Expand/collapse button */}
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(folder.id);
                }}
                style={expandButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}

            {/* Spacer for folders without children */}
            {!hasChildren && <div style={{ width: '20px' }} />}

            {/* Folder icon and name */}
            <span style={{ fontSize: '1.5rem' }}>{folder.icon || '📁'}</span>
            <span style={{
              flex: 1,
              fontSize: '1rem',
              color: '#374151',
              fontWeight: isSelected ? '600' : '400',
              textDecoration: isDisabled ? 'line-through' : 'none'
            }}>
              {folder.name}
            </span>

            {/* Selected checkmark */}
            {isSelected && !isDisabled && (
              <span style={{ color: '#667eea', fontSize: '1.25rem', fontWeight: 'bold' }}>✓</span>
            )}
          </div>

          {/* Render children if expanded */}
          {hasChildren && isExpanded && (
            <div style={{ marginLeft: '0.5rem', marginTop: '0.25rem' }}>
              {renderFolderTree(children, depth + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (!isOpen) return null;

  const isMovingToSameLocation = selectedFolderId === currentFolderId;

  // Styles
  const overlayStyle: CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    animation: 'fadeIn 0.2s ease'
  };

  const modalStyle: CSSProperties = {
    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%)',
    borderRadius: '24px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '2px solid rgba(102, 126, 234, 0.2)',
    animation: 'slideIn 0.3s ease',
    overflow: 'hidden'
  };

  const headerStyle: CSSProperties = {
    padding: '2rem',
    borderBottom: '2px solid rgba(102, 126, 234, 0.1)'
  };

  const titleStyle: CSSProperties = {
    margin: '0 0 1rem',
    fontSize: '1.75rem',
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  };

  const subtitleStyle: CSSProperties = {
    margin: 0,
    color: '#64748b',
    fontSize: '1rem'
  };

  const contentStyle: CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    padding: '1.5rem 2rem',
    background: 'rgba(248, 250, 252, 0.5)'
  };

  const rootOptionStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.875rem 1rem',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '1rem',
    background: selectedFolderId === null ? 'rgba(102, 126, 234, 0.15)' : 'white',
    border: selectedFolderId === null ? '2px solid #667eea' : '2px solid rgba(226, 232, 240, 0.8)',
    boxShadow: selectedFolderId === null ? '0 4px 12px rgba(102, 126, 234, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
  };

  const footerStyle: CSSProperties = {
    padding: '1.5rem 2rem',
    borderTop: '2px solid rgba(102, 126, 234, 0.1)',
    background: 'rgba(248, 250, 252, 0.8)'
  };

  const warningStyle: CSSProperties = {
    padding: '0.875rem 1rem',
    background: 'rgba(245, 158, 11, 0.1)',
    border: '2px solid rgba(245, 158, 11, 0.3)',
    borderRadius: '12px',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    color: '#92400e',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const buttonContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem'
  };

  const cancelButtonStyle: CSSProperties = {
    padding: '0.875rem 1.75rem',
    background: 'white',
    color: '#64748b',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    opacity: isSubmitting ? 0.6 : 1
  };

  const moveButtonStyle: CSSProperties = {
    padding: '0.875rem 1.75rem',
    background: (isSubmitting || isMovingToSameLocation)
      ? '#d1d5db'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: (isSubmitting || isMovingToSameLocation) ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: (isSubmitting || isMovingToSameLocation)
      ? 'none'
      : '0 4px 15px rgba(102, 126, 234, 0.4)',
    opacity: (isSubmitting || isMovingToSameLocation) ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  return (
    <>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div style={headerStyle}>
            <h2 style={titleStyle}>
              <span style={{ fontSize: '2rem' }}>📦</span>
              Move {itemType === 'folder' ? 'Folder' : 'Question Set'}
            </h2>
            <p style={subtitleStyle}>
              Moving: <strong style={{ color: '#374151' }}>{itemName}</strong>
            </p>
          </div>

          {/* Folder tree */}
          <div style={contentStyle}>
            {/* Root option */}
            <div
              style={rootOptionStyle}
              onClick={() => setSelectedFolderId(null)}
              onMouseEnter={(e) => {
                if (selectedFolderId !== null) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedFolderId !== null) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'translateX(0)';
                }
              }}
            >
              <span style={{ fontSize: '1.5rem' }}>📂</span>
              <span style={{
                flex: 1,
                fontWeight: '600',
                fontSize: '1rem',
                color: '#374151'
              }}>
                All Games (Root)
              </span>
              {selectedFolderId === null && (
                <span style={{ color: '#667eea', fontSize: '1.25rem', fontWeight: 'bold' }}>✓</span>
              )}
            </div>

            {/* Folder tree */}
            {folderTree.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {renderFolderTree(folderTree)}
              </div>
            ) : (
              <p style={{
                textAlign: 'center',
                color: '#94a3b8',
                padding: '2rem',
                fontSize: '0.95rem'
              }}>
                No folders available
              </p>
            )}
          </div>

          {/* Footer */}
          <div style={footerStyle}>
            {isMovingToSameLocation && (
              <div style={warningStyle}>
                <span style={{ fontSize: '1.1rem' }}>ℹ️</span>
                <span>This {itemType} is already in the selected location</span>
              </div>
            )}

            <div style={buttonContainerStyle}>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                style={cancelButtonStyle}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.color = '#667eea';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || isMovingToSameLocation}
                style={moveButtonStyle}
                onMouseEnter={(e) => {
                  if (!isSubmitting && !isMovingToSameLocation) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting && !isMovingToSameLocation) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                  }
                }}
              >
                {isSubmitting ? (
                  <>
                    <span style={{ animation: 'spin 1s linear infinite' }}>⏳</span>
                    Moving...
                  </>
                ) : (
                  <>
                    <span>📁</span>
                    Move Here
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};
